import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import PersonelModal from './PersonelModal';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const TIP_LABELS = { KADRO: 'Kadro', SERBEST: 'Serbest', PART_TIME: 'Part Time', EMEKLI: 'Emekli' };
const TIP_COLORS = {
  KADRO: { bg: '#e3f2fd', color: '#1565c0' },
  SERBEST: { bg: '#f3e5f5', color: '#6a1b9a' },
  PART_TIME: { bg: '#fff3e0', color: '#e65100' },
  EMEKLI: { bg: '#e8f5e9', color: '#2e7d32' },
};

// γ5 — Çıkış sebebi kod → Türkçe label
const CIKIS_SEBEP_LABELS = {
  ISVEREN_FESHI: 'İşveren Feshi',
  ISTIFA: 'İstifa',
  DENEME_FESHI: 'Deneme Feshi',
  EMEKLILIK: 'Emeklilik',
  ASKERLIK: 'Askerlik',
  EVLILIK: 'Evlilik',
  VEFAT: 'Vefat',
  HAKLI_NEDEN: 'Haklı Neden',
};

// Faz B — personel.durum (CALISIYOR / AYRILDI_ODENMEDI / PASIF) → rozet
const DURUM_BADGES = {
  CALISIYOR: { label: 'Çalışıyor', bg: '#e8f5e9', color: '#2e7d32' },
  AYRILDI_ODENMEDI: { label: 'Ayrıldı – ödeme bekliyor', bg: '#fff3e0', color: '#e65100' },
  PASIF: { label: 'Pasif', bg: '#f5f5f5', color: '#999' },
};

// Fallback: durum yoksa (migration öncesi eski kayıt) aktif bayrağından türet
const getDurumBadge = (p) => {
  if (p.durum && DURUM_BADGES[p.durum]) return DURUM_BADGES[p.durum];
  return p.aktif ? DURUM_BADGES.CALISIYOR : DURUM_BADGES.PASIF;
};

const PersonelListesiPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);

  const [personeller, setPersoneller] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [lokFilter, setLokFilter] = useState('');
  const [tipFilter, setTipFilter] = useUrlState('tip', '');
  const [aktifFilter, setAktifFilter] = useState('true');  // string 'true'/'false'/'tumu'
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (aktifFilter === 'true') qs.set('aktif', 'true');
      if (aktifFilter === 'false') qs.set('aktif', 'false');
      if (lokFilter) qs.set('lokasyon_id', lokFilter);
      if (tipFilter) qs.set('tip', tipFilter);

      const [personelRes, lokRes] = await Promise.all([
        fetch(`${API_URL}/api/personel?${qs}`, { headers }),
        fetch(`${API_URL}/api/lokasyonlar`, { headers }),
      ]);
      if (personelRes.ok) setPersoneller(await personelRes.json());
      else setPersoneller([]);
      // Backend /api/lokasyonlar artık TRANSIT'i default hariç tutuyor — ek filter gereksiz.
      if (lokRes.ok) setLokasyonlar(await lokRes.json());
    } catch (err) {
      logError('Personel yüklenemedi:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktifFilter, lokFilter, tipFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = personeller.filter(p => {
    if (!arama) return true;
    const s = normalizeForSearch(arama);
    return searchMatchPre(p.ad_soyad, s)
        || searchMatchPre(p.pozisyon, s);
  });

  const kidem = (ise_giris) => {
    if (!ise_giris) return '';
    const yil = (new Date() - new Date(ise_giris)) / (1000 * 60 * 60 * 24 * 365);
    return yil < 1 ? `${Math.round(yil * 12)} ay` : `${yil.toFixed(1)} yıl`;
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`"${p.ad_soyad}" pasife çekilecek. Tüm kayıtlar (puantaj, izin vb.) korunacak. Emin misin?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/personel/${p.id}`, { method: 'DELETE', headers });
      if (r.ok) {
        toast.success('Pasife alındı');
        invalidateQueries(['personel']);
        fetchData();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  // Kalıcı silme (yalnız PASIF + usage=0). Mükerrer/yanlış kayıt temizliği için.
  const handleHardDelete = async (p) => {
    if (!window.confirm(`"${p.ad_soyad}" KALICI olarak silinecek. Bu işlem geri ALINAMAZ.\n\nYalnızca hiç puantaj/izin/avans/kıdem kaydı olmayan (mükerrer/yanlış) kayıtlar silinebilir. Emin misin?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/personel/${p.id}?hard=true`, { method: 'DELETE', headers });
      if (r.ok) {
        toast.success('Kalıcı olarak silindi');
        invalidateQueries(['personel']);
        fetchData();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  if (!canManage) {
    return (
      <div>
        <Header title="Personel Listesi" />
        <div style={{ padding: 40, textAlign: 'center', color: colors.textSecondary }}>
          Bu sayfayı görüntüleme yetkiniz yok.
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Personel Listesi" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Filtre barı */}
        <div style={{ background: colors.surface, padding: 14, borderRadius: radius.lg, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', boxShadow: shadows.sm }}>
          <input
            type="text"
            placeholder="Ad veya pozisyon ara..."
            value={arama}
            onChange={e => setArama(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14, minWidth: 220 }}
          />
          <select value={lokFilter} onChange={e => setLokFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}>
            <option value="">Tüm Lokasyonlar</option>
            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
          </select>
          <select value={tipFilter} onChange={e => setTipFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}>
            <option value="">Tüm Tipler</option>
            <option value="KADRO">Kadro</option>
            <option value="SERBEST">Serbest</option>
            <option value="PART_TIME">Part Time</option>
            <option value="EMEKLI">Emekli</option>
          </select>
          {/* γ5 — 3'lü buton grubu */}
          <div style={{ display: 'inline-flex', border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
            {[
              { v: 'true', l: 'Aktif' },
              { v: 'false', l: 'Pasif' },
              { v: 'tumu', l: 'Tümü' },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setAktifFilter(opt.v)}
                style={{
                  padding: '8px 14px',
                  background: aktifFilter === opt.v ? '#1565c0' : 'white',
                  color: aktifFilter === opt.v ? 'white' : '#555',
                  border: 'none',
                  borderLeft: opt.v !== 'true' ? `1px solid ${colors.border}` : 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: aktifFilter === opt.v ? 700 : 500,
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 13, color: '#888' }}>{filtered.length} personel</span>
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            style={{ marginLeft: 'auto', padding: '9px 18px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
          >
            + Yeni Personel
          </button>
        </div>

        {/* Kart grid */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.textTertiary }}>Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: colors.textTertiary, background: colors.surface, borderRadius: radius.md }}>
            Personel bulunamadı
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {filtered.map(p => {
              const tc = TIP_COLORS[p.tip] || TIP_COLORS.KADRO;
              const durumBadge = getDurumBadge(p);
              const pasif = !p.aktif;
              const cikisSebepStr = p.cikis_sebebi
                ? (p.cikis_sebebi_sgk_kodu
                  ? `${CIKIS_SEBEP_LABELS[p.cikis_sebebi] || p.cikis_sebebi} (Kod ${p.cikis_sebebi_sgk_kodu})`
                  : (CIKIS_SEBEP_LABELS[p.cikis_sebebi] || p.cikis_sebebi))
                : null;
              return (
                <div key={p.id} style={{
                  background: pasif ? '#f5f5f5' : colors.surface,
                  padding: 18,
                  borderRadius: radius.md,
                  boxShadow: shadows.sm,
                  borderLeft: `4px solid ${p.aktif ? '#2e7d32' : '#999'}`,
                  opacity: p.aktif ? 1 : 0.85,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: '#333' }}>{p.ad_soyad}</h3>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: radius.lg, background: durumBadge.bg, color: durumBadge.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {durumBadge.label}
                      </span>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: radius.lg, background: tc.bg, color: tc.color, fontWeight: 700 }}>
                        {TIP_LABELS[p.tip] || p.tip}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
                    {p.pozisyon || '(pozisyon yok)'} · {p.lokasyon_ad || 'Lokasyon yok'}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 6 }}>
                    İşe giriş: {p.ise_giris_tarihi} ({kidem(p.ise_giris_tarihi)})
                  </div>
                  {pasif && (
                    <div style={{ marginTop: 8, padding: 8, background: '#fff3e0', borderRadius: radius.sm, border: '1px solid #ffb74d' }}>
                      {p.ise_cikis_tarihi && (
                        <div style={{ fontSize: 11, color: '#e65100', fontWeight: 600 }}>
                          Çıkış Tarihi: {p.ise_cikis_tarihi}
                        </div>
                      )}
                      {cikisSebepStr && (
                        <div style={{ fontSize: 11, color: '#5d4037', marginTop: 2 }}>
                          Sebep: <strong>{cikisSebepStr}</strong>
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#5d4037', marginTop: 2 }}>
                        Kıdem Tutarı: <span style={{ color: '#999' }}>—</span>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, marginTop: 10, color: '#555' }}>
                    Maaş: <strong>{(p.varsayilan_brut_maas || 0).toLocaleString('tr-TR')} ₺</strong>
                    {' · '}Yol: {(p.gunluk_yol_ucreti || 0).toLocaleString('tr-TR')} ₺/gün
                  </div>
                  <div style={{ marginTop: 14, display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => { setEditing(p); setModalOpen(true); }}
                      style={{ flex: 1, padding: 7, background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => navigate(`/ik?tab=puantaj&personel_id=${p.id}`)}
                      style={{ flex: 1, padding: 7, background: colors.surface, color: '#1565c0', border: '1px solid #1565c0', borderRadius: radius.sm, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      Maaş Kartı
                    </button>
                    {p.aktif ? (
                      <button
                        onClick={() => handleDelete(p)}
                        style={{ padding: '7px 12px', background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Pasife Al
                      </button>
                    ) : (
                      <button
                        onClick={() => handleHardDelete(p)}
                        title="Kalıcı sil — yalnız hiç puantaj/izin/avans kaydı olmayan (mükerrer/yanlış) kayıtlar"
                        style={{ padding: '7px 12px', background: '#c62828', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {modalOpen && (
          <PersonelModal
            personel={editing}
            lokasyonlar={lokasyonlar}
            onClose={() => { setModalOpen(false); setEditing(null); }}
            onSaved={() => { setModalOpen(false); setEditing(null); fetchData(); }}
          />
        )}
      </div>
    </div>
  );
};

export default PersonelListesiPage;
